import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, LandPlot, Handshake, Building } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import CTABand from "@/components/CTABand";

const PILLARS = [
  {
    icon: LandPlot,
    title: "Plot acquisition",
    text: "Identification and evaluation of land parcels across the UAE — residential, hospitality and mixed-use — with zoning, access and title diligence coordinated on your behalf.",
  },
  {
    icon: Handshake,
    title: "Structured partnerships",
    text: "Joint-venture frameworks between landowners, investors and developers, structured with independent legal counsel and aligned incentives from day one.",
  },
  {
    icon: Building,
    title: "Development-led routes",
    text: "From feasibility conversations to master-developer introductions, ACRE helps selected investors think beyond acquisition towards what a plot could become.",
  },
];

const LandJV = () => (
  <div data-testid="land-jv-page">
    <SEO
      title="Land, Development & JV Opportunities in the UAE"
      description="ACRE works with investors, developers, landowners and family offices exploring plot acquisition, structured partnerships and development-led opportunities across the UAE."
      path="/land-development-jv"
      keywords="UAE land investment, development joint venture Dubai, Al Marjan Island property investment, tax-free property investment UAE"
    />
    <PageHero
      eyebrow="Land · Development · JV"
      title="Land, Development & JV Opportunities in the UAE"
      intro="ACRE works with investors, developers, landowners and family offices exploring plot acquisition, structured partnerships and development-led opportunities across the UAE."
      image="https://images.pexels.com/photos/1470403/pexels-photo-1470403.jpeg?auto=compress&cs=tinysrgb&q=85"
      testid="land-jv-hero"
    />

    <section className="pb-24" data-testid="plot-to-partnership-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <p className="eyebrow mb-4">From Plot Purchase to Development Partnership</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light text-[#F4F3EF] max-w-3xl leading-tight mb-6">
            Think beyond acquisition
          </h2>
          <p className="text-lg text-[#9DA3AE] font-light leading-relaxed max-w-3xl mb-14">
            From identifying the right plot to exploring how that land could move towards development, ACRE helps
            selected investors think beyond acquisition. The conversation begins with intent — hold, build, partner or
            trade — and is shaped by evidence at every step.
          </p>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6">
          {PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.12}>
              <div className="card-hover h-full bg-[#13151D] border border-[rgba(197,168,128,0.15)] rounded-2xl p-8" data-testid={`land-pillar-${i + 1}`}>
                <p.icon size={28} className="text-[#E6CA65] mb-6" strokeWidth={1.5} />
                <h3 className="font-display text-2xl text-[#F4F3EF] mb-4">{p.title}</h3>
                <p className="text-sm text-[#9DA3AE] font-light leading-relaxed">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal>
          <div className="mt-14 bg-[#0E382B]/20 border border-[rgba(197,168,128,0.2)] rounded-2xl p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <p className="text-[#c9cdd6] font-light max-w-xl">
              Land and JV conversations are held privately and by introduction. Tell us about your mandate and we will
              respond with a considered view.
            </p>
            <Link
              to="/private-consultation"
              data-testid="land-jv-cta-button"
              className="shrink-0 inline-flex items-center gap-2 text-sm uppercase tracking-[0.14em] font-semibold px-8 py-4 rounded-full text-[#0B0C10] bg-gradient-to-r from-[#E6CA65] via-[#C5A880] to-[#9A7B4F] hover:shadow-[0_0_24px_rgba(197,168,128,0.45)] transition-shadow duration-300"
            >
              Start the Conversation <ArrowRight size={16} />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>

    <CTABand testid="land-jv-final-cta" />
  </div>
);

export default LandJV;
